import React, { FC, useContext, useMemo, useState } from 'react';

import { Descendant } from 'slate';

import LottieInsert from '../../../components/shared/AampeLottieDisplay/LottieInsert';
import styled from 'styled-components';

//constants
import aampeTheme from '../../../constants/aampeTheme';
import ANIMATION from '../../../constants/animation';
import { FormulaType } from '../../../constants/formulaType';

//context
import { EditorContext } from '../../../context/EditorContext';

//helpers
import { transformHeaderData } from './helpers';

import useComponentTypesQuery from '../../../hooks/component-types/useComponentTypesQuery';

//models
import { AlternateHeaderType } from '../../../models/formula.model';

//components
import FormulaAlternateHeaderDataView from './FormulaAlternateHeaderDataView';

interface FormulaAlternateMainProps {
    formulaStructure: Descendant[];
    formulaId: number;
    isEditable: boolean;
}
const FormulaAlternateMain: FC<FormulaAlternateMainProps> = ({
    formulaStructure,
    formulaId,
    isEditable,
}) => {
    const {
        editorDetails: { formulaType },
    } = useContext(EditorContext);

    const { data: componentTypes } = useComponentTypesQuery();

    const headerData = useMemo(() => {
        return transformHeaderData(
            formulaStructure,
            formulaType as FormulaType,
            componentTypes?.map ?? {},
        );
    }, [formulaStructure, formulaType, componentTypes]);

    const [alternateCreateError, setAlternateCreateError] = useState<
        string | null
    >(null);
    return (
        <>
            {!headerData?.headers?.length ? (
                <StyledLottieInsert
                    emptyStateStyle={{
                        color: aampeTheme.colors.neutral09,
                        fontSize: aampeTheme.size.font.textMedium,
                        maxWidth: '640px',
                        fontWeight: 400,
                        lineHeight: '24px',
                    }}
                    lottie={ANIMATION.copyWriting}
                    text="Add content to your message in step 2, after that the alternates table will appear here where you can see all the message combinations and make adjustments to your alternates."
                />
            ) : (
                <Container>
                    {headerData?.headers.map(
                        (headerData: AlternateHeaderType, index: number) => (
                            <Content key={index}>
                                <HeaderText>{headerData.headerName}</HeaderText>

                                <FormulaAlternateHeaderDataView
                                    headerData={headerData}
                                    formulaId={formulaId}
                                    setAlternateCreateError={
                                        setAlternateCreateError
                                    }
                                    alternateCreateError={alternateCreateError}
                                    isEditable={isEditable}
                                />
                            </Content>
                        ),
                    )}
                </Container>
            )}
        </>
    );
};

const Container = styled.div({
    border: `1px solid ${aampeTheme.colors.interactiveWG01}`,
    borderLeft: '0px',
    display: 'flex',
    minHeight: '460px',
});

const Content = styled.div({
    display: 'flex',
    flexDirection: 'column',
});

const HeaderText = styled.div({
    display: 'flex',
    textTransform: 'capitalize',
    alignItems: 'center',
    gap: aampeTheme.spacing4(2),
    padding: aampeTheme.spacing4(2, 4),
    fontSize: aampeTheme.size.font.textMedium,
    border: `1px solid ${aampeTheme.colors.interactiveWG01}`,
    borderRight: '0px',
    borderTop: '0px',
    lineHeight: '24px',
    fontWeight: 500,
    color: aampeTheme.colors.textAccessibleWG01,
    backgroundColor: aampeTheme.colors.backgroundWG02,
});

const StyledLottieInsert = styled(LottieInsert)({
    height: '100%',
    width: '100%',
    display: 'flex',
    padding: aampeTheme.spacing4(12, 8, 60, 8),
    gap: aampeTheme.spacing4(16),
});

export default FormulaAlternateMain;
